import {
  AdminApiError,
  type AdminClient,
  type BetView,
  type RoundExplorerView,
  type VerifyResponse,
} from '@apx/admin-sdk';
import { wireDatePickers } from '../date-picker.js';
import { applyHiddenCols, mountColumnPicker } from '../column-picker.js';
import { customModal, html } from '../modal.js';
import { fmtMoney, fmtTime } from '../format.js';
import { exportXlsx } from '../xlsx.js';
import { applySort, getSortState, sortableTh, wireSortHeaders } from '../sort.js';
import { mountSavedViews } from '../saved-views.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { gameOptions, prettyGame } from '../games.js';
import { acceptStatusCell } from './reports-shared.js';

export interface RoundsExplorerDeps {
  client: AdminClient;
  integratorId: string;
  roundEngineBaseUrl: string;
}

export async function renderRoundsExplorerPage(
  root: HTMLElement,
  deps: RoundsExplorerDeps,
): Promise<void> {
  const url = new URL(window.location.href);
  const qsGame = url.searchParams.get('gameId') ?? '';
  const qsState = url.searchParams.get('state') ?? '';
  const qsIncomplete = url.searchParams.get('incomplete') === '1' || url.searchParams.get('status') === 'open';
  const initialState = qsIncomplete ? '__incomplete__' : qsState;

  const isIncompleteView = qsIncomplete;
  const pageTitle = isIncompleteView ? 'Incomplete games' : 'Round explorer';
  const pageSub = isIncompleteView
    ? 'Rounds that have not reached FINISHED — open betting, mid-play, or stuck. Click any row to drill into bets + run the verifier.'
    : 'Searchable round history. Click any row to drill into bets + run the public provably-fair verifier inline.';

  root.innerHTML = `
    <h1 class="page-title">${escapeHtml(pageTitle)}</h1>
    <p class="page-sub">${pageSub}</p>

    <div class="card">
      <h2>Filters</h2>
      <div class="row">
        <label>Game<select id="re-game">${gameOptions(qsGame, true)}</select></label>
        <label>State
          <select id="re-state">
            <option value="">(any)</option>
            <option value="__incomplete__"${initialState === '__incomplete__' ? ' selected' : ''}>(incomplete — any non-finished)</option>
            <option value="betting_open"${initialState === 'betting_open' ? ' selected' : ''}>betting_open</option>
            <option value="playing"${initialState === 'playing' ? ' selected' : ''}>playing</option>
            <option value="revealing"${initialState === 'revealing' ? ' selected' : ''}>revealing</option>
            <option value="showing_result"${initialState === 'showing_result' ? ' selected' : ''}>showing_result</option>
            <option value="finished"${initialState === 'finished' ? ' selected' : ''}>finished</option>
          </select>
        </label>
        <span class="filter-field"><label for="re-from">From</label><input id="re-from" type="datetime-local" /></span>
        <span class="filter-field"><label for="re-to">To</label><input id="re-to" type="datetime-local" /></span>
        <button id="re-run" class="primary">Search</button>
        <button id="re-export" class="ghost">Export Excel</button>
        <div id="re-cols"></div>
        <div id="re-views" class="row" style="margin:0 0 0 auto;gap:6px"></div>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:minmax(0,1.8fr) minmax(280px,1fr);gap:16px;align-items:flex-start">
      <div class="card" style="margin-bottom:0">
        <h2>Results</h2>
        <div id="re-list" style="max-height:60vh;overflow:auto">${skeletonTable({ rows: 10, columns: 6 })}</div>
      </div>
      <div class="card" style="margin-bottom:0;position:sticky;top:16px;max-height:80vh;overflow:auto">
        <h2>Drill-down</h2>
        <div id="re-detail"><p class="muted">Pick a round on the left to inspect bets + verify seeds.</p></div>
      </div>
    </div>
  `;

  wireDatePickers(root);

  const listEl = root.querySelector<HTMLElement>('#re-list')!;
  const detailEl = root.querySelector<HTMLElement>('#re-detail')!;

  let accumulated: RoundExplorerView[] = [];
  let lastCursor: string | undefined;
  let lastGameId: string | undefined;
  let lastState: string | undefined;
  let lastIncomplete: boolean | undefined;
  let lastFrom: string | undefined;
  let lastTo: string | undefined;

  function readDateInput(id: string): string | undefined {
    const raw = root.querySelector<HTMLInputElement>(id)!.value.trim();
    if (!raw) return undefined;
    // datetime-local emits "YYYY-MM-DDTHH:mm" (no tz). Convert to ISO so the
    // backend's z.string().datetime() accepts it.
    return new Date(raw).toISOString();
  }

  async function refresh(append = false): Promise<void> {
    if (!append) {
      accumulated = [];
      lastCursor = undefined;
      lastGameId = root.querySelector<HTMLSelectElement>('#re-game')!.value.trim() || undefined;
      const rawState = root.querySelector<HTMLSelectElement>('#re-state')!.value;
      if (rawState === '__incomplete__') {
        lastState = undefined;
        lastIncomplete = true;
      } else {
        lastState = rawState || undefined;
        lastIncomplete = undefined;
      }
      lastFrom = readDateInput('#re-from');
      lastTo = readDateInput('#re-to');
      listEl.innerHTML = skeletonTable({ rows: 10, columns: 6 });
    }
    try {
      const res = await deps.client.listRounds(deps.integratorId, {
        gameId: lastGameId,
        state: lastState,
        incomplete: lastIncomplete,
        from: lastFrom,
        to: lastTo,
        cursor: append ? lastCursor : undefined,
        limit: 30,
      });
      accumulated = accumulated.concat(res.rounds);
      lastCursor = res.nextCursor;
      listEl.innerHTML = `${renderTable(accumulated)}
        <div class="row" style="margin-top:10px;padding:0 10px 10px">
          ${lastCursor ? '<button id="re-load-more">Load more</button>' : '<span class="muted">End of list.</span>'}
          <span class="muted" style="margin-left:auto">${accumulated.length} loaded</span>
        </div>`;
      bindRows(accumulated);
      listEl
        .querySelector<HTMLButtonElement>('#re-load-more')
        ?.addEventListener('click', () => void refresh(true));
      applyHiddenCols(listEl.querySelector<HTMLElement>('table'), 'rounds-explorer');
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(rounds: RoundExplorerView[]): string {
    if (rounds.length === 0) return '<p class="muted">No rounds match.</p>';
    // Dropped: Game column (always the integrator-selected game). State + Result
    // packed into one cell to free horizontal space; the side-by-side grid
    // halves the available card width.
    const sort = getSortState('rounds');
    const sorted = applySort(rounds, sort, (r, key) => {
      switch (key) {
        case 'id':
          return r.id;
        case 'gameId':
          return r.gameId;
        case 'startedAt':
          return r.startedAt;
        case 'state':
          return r.state;
        case 'totalBet':
          return r.totalBet;
        case 'totalPayout':
          return r.totalPayout;
        case 'participantCount':
          return r.participantCount;
        default:
          return '';
      }
    });
    return `<table>
      <thead><tr>
        ${sortableTh('Round', 'id', sort)}
        ${sortableTh('Game', 'gameId', sort)}
        ${sortableTh('When', 'startedAt', sort)}
        ${sortableTh('State / result', 'state', sort)}
        ${sortableTh('Bet', 'totalBet', sort)}
        ${sortableTh('Payout', 'totalPayout', sort)}
        ${sortableTh('Plyrs', 'participantCount', sort)}
      </tr></thead>
      <tbody>${sorted
        .map((r) => {
          const stateClass = r.state === 'betting_open' ? 'live' : r.state === 'finished' ? '' : 'warn';
          const resultLabel = describeResult(r.result);
          return `<tr class="re-row" data-inspect-row="${r.id}" style="cursor:pointer" title="Inspect bets + verify seeds">
            <td><code>${r.id.slice(-8)}</code></td>
            <td><span class="pill" style="font-size:10px">${escapeHtml(prettyGame(r.gameId))}</span></td>
            <td><span class="muted">${fmtTime(r.startedAt)}</span></td>
            <td>
              <span class="pill ${stateClass}" style="font-size:10px">${r.state}</span>
              <div style="margin-top:4px;max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"><code>${escapeHtml(resultLabel)}</code></div>
            </td>
            <td>${Number(r.totalBet).toLocaleString()}</td>
            <td>${Number(r.totalPayout).toLocaleString()}</td>
            <td>${r.participantCount}</td>
          </tr>`;
        })
        .join('')}</tbody>
    </table>`;
  }

  function bindRows(_rounds: RoundExplorerView[]): void {
    listEl.querySelectorAll<HTMLElement>('tr[data-inspect-row]').forEach((row) => {
      row.addEventListener('click', () => void inspect(row.dataset.inspectRow!));
    });
    const table = listEl.querySelector<HTMLElement>('table');
    if (table) {
      wireSortHeaders(table, 'rounds', () => {
        const wrapper = listEl;
        wrapper.innerHTML = `${renderTable(accumulated)}
          <div class="row" style="margin-top:10px;padding:0 10px 10px">
            ${lastCursor ? '<button id="re-load-more">Load more</button>' : '<span class="muted">End of list.</span>'}
            <span class="muted" style="margin-left:auto">${accumulated.length} loaded</span>
          </div>`;
        bindRows(accumulated);
        listEl
          .querySelector<HTMLButtonElement>('#re-load-more')
          ?.addEventListener('click', () => void refresh(true));
        applyHiddenCols(listEl.querySelector<HTMLElement>('table'), 'rounds-explorer');
      });
    }
  }

  async function inspect(roundId: string): Promise<void> {
    detailEl.innerHTML = '<p class="muted">Loading bets + running verifier…</p>';
    try {
      const [{ bets }, verify] = await Promise.all([
        deps.client.listRoundBets(roundId),
        deps.client
          .verifyRound(deps.roundEngineBaseUrl, roundId)
          .catch((err) => ({ error: describe(err) }) as { error: string }),
      ]);
      detailEl.innerHTML = `
        <h3 style="margin:0 0 10px">Round <code>${roundId}</code></h3>
        ${renderVerify(verify)}
        <h4 style="margin:18px 0 8px">Bets (${bets.length})</h4>
        ${renderBetTable(bets)}
      `;
      bindRefundButtons(roundId);
    } catch (err) {
      detailEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function bindRefundButtons(roundId: string): void {
    detailEl.querySelectorAll<HTMLButtonElement>('button[data-refund]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const betId = btn.dataset.refund!;
        const creditAmount = btn.dataset.credit ?? '0';
        // The bet's currency; its stake is integer minor units of it.
        const betCurrency = btn.dataset.currency || undefined;
        const playerId = btn.dataset.player ?? '';
        const betType = btn.dataset.bettype ?? '';
        const parsed = await customModal<{ reason: string }>({
          title: 'Refund bet',
          confirmLabel: 'Refund via integrator rollback',
          danger: true,
          body: html`
            <div style="display:flex;flex-direction:column;gap:14px">
              <div class="muted" style="font-size:12px">
                Calls integrator <code>/wallet/rollback</code> with the original bet
                <code>requestId</code>. Idempotent — if the rollback was already
                applied, the integrator returns the prior result. Bet is marked
                <code>VOID</code> locally.
              </div>
              <div style="display:grid;grid-template-columns:120px 1fr;gap:6px 12px;font-size:12px">
                <div class="muted">Bet id</div><div><code>${betId}</code></div>
                <div class="muted">Player</div><div><code>${playerId}</code></div>
                <div class="muted">Pick</div><div><code>${betType}</code></div>
                <div class="muted">Amount</div><div style="font-variant-numeric:tabular-nums;font-weight:600">${fmtMoney(creditAmount, betCurrency)} ${betCurrency ?? ''}</div>
                <div class="muted">Rollback requestId</div><div><code>refund:${betId}</code></div>
              </div>
              <label style="display:flex;flex-direction:column;gap:4px">
                <span class="muted" style="font-size:11px">Reason (audit-logged)</span>
                <input id="re-refund-reason" type="text" placeholder="e.g. support, dispute resolved" />
              </label>
            </div>
          `,
          parseOnConfirm: (root) => {
            const reason = root.querySelector<HTMLInputElement>('#re-refund-reason')!.value.trim();
            if (!reason) {
              showToast('Reason required.', 'warn');
              return null;
            }
            return { reason };
          },
        });
        if (!parsed) return;
        btn.disabled = true;
        try {
          const res = await deps.client.refundBet(betId, parsed.reason);
          showToast(
            `Refunded ${Number(res.refundAmount).toLocaleString()}. New balance ${Number(res.newBalance).toLocaleString()}.`,
            'success',
          );
          await inspect(roundId);
        } catch (err) {
          btn.disabled = false;
          showToast(describe(err), 'error');
        }
      });
    });
  }

  function renderVerify(v: VerifyResponse | { error: string }): string {
    if ('error' in v) {
      return `<p class="muted">Verifier: ${escapeHtml(v.error)} (round may still be in flight).</p>`;
    }
    // Tri-state verdict from server. Older rounds without a
    // configSnapshot in their RoundAudit can't be verified against ground-
    // truth weights, so show "UNVERIFIABLE" (warn) instead of "INVALID"
    // (error). VALID stays green, true mismatches stay red.
    const verdict: string = (v as { verdict?: string }).verdict ?? (v.valid ? 'VALID' : 'INVALID');
    const verdictColor =
      verdict === 'VALID'
        ? 'var(--success)'
        : verdict === 'UNVERIFIABLE'
          ? 'var(--warn, #fde047)'
          : 'var(--error)';
    const verdictNote =
      verdict === 'UNVERIFIABLE'
        ? ' <span class="muted" style="font-weight:400;font-size:11px">(no configSnapshot — settled before C.3 audit)</span>'
        : '';
    // Result shape varies by game — greedyking is { primary, special }, others
    // return a plain string or a game-specific object. Render any of them
    // without assuming the two-tier shape (else .primary / .toFixed crash).
    const expectedStr = describeResult(v.expectedResult);
    const derivedStr = describeResult(v.derivedResult);
    const rollBits: string[] = [];
    if (typeof v.derivedPrimaryRoll === 'number')
      rollBits.push(`primary roll ${v.derivedPrimaryRoll.toFixed(6)}`);
    if (typeof v.derivedSpecialRoll === 'number')
      rollBits.push(`special roll ${v.derivedSpecialRoll.toFixed(6)}`);
    const rollNote = rollBits.length ? ` (${rollBits.join(', ')})` : '';
    return `<div class="kv">
      <dt>Verifier verdict</dt><dd style="color:${verdictColor};font-weight:600">${verdict}${verdictNote}</dd>
      <dt>Expected</dt><dd><code>${escapeHtml(expectedStr)}</code></dd>
      <dt>Derived</dt><dd><code>${escapeHtml(derivedStr)}</code>${rollNote}</dd>
      <dt>Server seed</dt><dd><code>${escapeHtml(v.serverSeed)}</code></dd>
      <dt>Server seed hash</dt><dd><code>${escapeHtml(v.serverSeedHash)}</code></dd>
      <dt>Client seed</dt><dd><code>${escapeHtml(v.clientSeed)}</code></dd>
      <dt>Nonce</dt><dd>${v.nonce ?? '—'}</dd>
    </div>`;
  }

  function renderBetTable(bets: BetView[]): string {
    if (bets.length === 0) return '<p class="muted">No bets on this round.</p>';
    return `<table>
      <thead><tr><th>Player</th><th>Pick</th><th>Currency</th><th style="text-align:right">Bet</th><th>Outcome</th><th>Accept status</th><th style="text-align:right">Won</th><th>Placed</th><th></th></tr></thead>
      <tbody>${bets
        .map(
          (b) => `<tr class="row-nav" data-row-href="/integrators/${encodeURIComponent(deps.integratorId)}/players/${b.playerId}">
            <td><code style="color:var(--accent-2)">${b.playerId.slice(0, 8)}…</code></td>
            <td><code>${b.betType}</code></td>
            <td><code>${escapeHtml(b.currency ?? '—')}</code></td>
            <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(b.creditAmount, b.currency)}</td>
            <td><span class="pill ${b.outcome === 'WIN' ? 'live' : b.outcome === 'VOID' ? 'warn' : ''}">${b.outcome}</span></td>
            ${acceptStatusCell(b.acceptStatus)}
            <td style="text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(b.creditPayout, b.currency)}</td>
            <td>${fmtTime(b.placedAt)}</td>
            <td>${b.outcome === 'VOID' ? '<span class="muted" style="font-size:11px">refunded</span>' : `<button data-refund="${b.id}" data-credit="${b.creditAmount}" data-currency="${escapeHtml(b.currency ?? '')}" data-player="${b.playerId}" data-bettype="${escapeHtml(b.betType)}" class="ghost" style="padding:4px 8px;font-size:12px">Refund</button>`}</td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
  }

  root.querySelector<HTMLButtonElement>('#re-run')!.addEventListener('click', () => void refresh());

  mountColumnPicker(root.querySelector<HTMLElement>('#re-cols')!, {
    pageKey: 'rounds-explorer',
    columns: ['Round', 'Game', 'When', 'State', 'Bet', 'Payout', 'Players', 'Actions'],
    onChange: () => applyHiddenCols(listEl.querySelector<HTMLElement>('table'), 'rounds-explorer'),
  });

  mountSavedViews(root.querySelector<HTMLElement>('#re-views')!, 'rounds-explorer', {
    capture: () => ({
      gameId: root.querySelector<HTMLSelectElement>('#re-game')!.value,
      state: root.querySelector<HTMLSelectElement>('#re-state')!.value,
      from: root.querySelector<HTMLInputElement>('#re-from')!.value,
      to: root.querySelector<HTMLInputElement>('#re-to')!.value,
    }),
    apply: (values) => {
      root.querySelector<HTMLSelectElement>('#re-game')!.value = values.gameId ?? '';
      root.querySelector<HTMLSelectElement>('#re-state')!.value = values.state ?? '';
      root.querySelector<HTMLInputElement>('#re-from')!.value = values.from ?? '';
      root.querySelector<HTMLInputElement>('#re-to')!.value = values.to ?? '';
      void refresh();
    },
  });

  root.querySelector<HTMLButtonElement>('#re-export')!.addEventListener('click', () => {
    if (accumulated.length === 0) {
      showToast('No rounds to export.', 'warn');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    exportXlsx(
      `apx-rounds-${stamp}.xlsx`,
      accumulated.map((r) => ({
        id: r.id,
        gameId: r.gameId,
        state: r.state,
        startedAt: r.startedAt,
        bettingClosesAt: r.bettingClosesAt,
        endedAt: r.endedAt ?? '',
        totalBet: r.totalBet,
        totalPayout: r.totalPayout,
        participantCount: r.participantCount,
        result: JSON.stringify(r.result),
        serverSeedHash: r.serverSeedHash ?? '',
      })),
    );
    showToast(`Exported ${accumulated.length} rounds.`, 'success');
  });

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

/**
 * Render a round/verifier result of unknown shape. Games differ: greedyking
 * uses `{ primary, special }`, others a plain string or a bespoke object.
 * Never `String(obj)` (→ "[object Object]") — fall back to compact JSON.
 */
export function describeResult(result: unknown): string {
  if (result == null) return '—';
  if (typeof result === 'string') return result.replace(/^"|"$/g, '');
  if (typeof result !== 'object') return String(result);
  const r = result as {
    primary?: unknown;
    special?: unknown;
    dice?: number[];
    total?: number;
    winner?: string;
    sum?: number;
    playerTotal?: number;
    bankerTotal?: number;
  };
  // greedyking — wheel slot (+ optional special).
  if (r.primary !== undefined) {
    return `${String(r.primary)}${r.special ? ` + ${String(r.special)}` : ''}`;
  }
  // sicbo — three dice + total.
  if (Array.isArray(r.dice)) {
    return `${r.dice.join('·')}${r.total !== undefined ? ` = ${r.total}` : ''}`;
  }
  // baccarat — winning side + both hand totals. Checked BEFORE the dragontiger
  // branch: a baccarat result also carries `winner`, so it fell through to that
  // branch and printed a bare "PLAYER" with no scoreline (its `sum` is undefined
  // — the totals live on playerTotal/bankerTotal).
  if (typeof r.winner === 'string' && r.playerTotal !== undefined && r.bankerTotal !== undefined) {
    return `${r.winner} ${r.playerTotal}–${r.bankerTotal}`;
  }
  // dragontiger — winning side (+ sum).
  if (typeof r.winner === 'string') {
    return `${r.winner}${r.sum !== undefined ? ` (${r.sum})` : ''}`;
  }
  // Unknown shape — compact JSON, capped so it can't blow out the table.
  try {
    const s = JSON.stringify(result);
    return s.length > 80 ? `${s.slice(0, 77)}…` : s;
  } catch {
    return String(result);
  }
}

function escapeHtml(s: string | null | undefined): string {
  return (s ?? '').toString().replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
